import React, { useEffect, useState } from "react";
import { YStack, XStack, Text, View, Circle } from "tamagui";
import { RiArrowLeftSLine, RiFullscreenLine, RiPhoneLine, RiTruckLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { colors } from "@styles/colors";
import { isGasSkinV2, SkinCard, v2 } from "@screen/Home/pages/GasShop/skin";
import { toTelHref } from "@utils/func/supplierIdentity";
import TrackingMap from "./TrackingMap";
import TrackingSheet from "./TrackingSheet";
import { useDriverLocation } from "./useDriverLocation";
import { buildTrackingDisplay } from "./trackingDisplay";
import { useTrackingHero } from "@utils/store/tracking/useTrackingHero";
import type { LatLngPoint } from "./smoothing";

/**
 * 配送追蹤（訂單詳情頁的頁首）。
 *
 * 有地圖時，地圖就是這一頁的上半頁——不是一張卡片裡的縮圖。
 * 理由：客戶點進一張配送中的訂單，第一個也往往是唯一的問題是「他到哪了」；
 * 金額與明細在這個當下都是次要的（而且他下單時就看過了）。
 * 所以配送中的詳情頁把版面讓給地圖，其餘資訊往下順移；訂單一離開配送中，
 * 整塊消失，頁面回到原本以狀態卡開頭的樣子。
 */

/**
 * 地圖高度：標題列以下到畫面中線，也就是整頁的上半部。
 *
 * 地圖從畫面最上緣起算（標題列浮在它上面），62vh 大約到畫面下方三分之一處。
 * 上限是「資訊卡與它下面那張狀態卡還留得住首屏的一角」——完全看不到下面有東西，
 * 客戶會以為這頁只有一張地圖，不會往下捲。
 * 用 vh 讓它跟著螢幕走，但兩端夾住：太矮則兩個點擠在一起，看不出相對位置。
 */
const HERO_HEIGHT = "clamp(320px, 62vh, 680px)";

/**
 * 讓地圖左右滿版。
 *
 * 內容容器有 18px 內距，而地圖貼著邊緣才有「這是一張地圖」而不是「一張放了地圖的卡」
 * 的感覺。用 100vw + 負 margin 而不是寫死 -18：內距值改了這裡不用跟著改。
 * 上緣不必處理——頁首是地圖時，PageWrapper 與訂單詳情都不會加上內距（見 useTrackingHero）。
 */
const fullBleed: React.CSSProperties = {
  width: "100vw",
  marginLeft: "calc(50% - 50vw)",
  position: "relative",
  // 疊在下方的資訊卡要蓋過地圖；Leaflet 的圖層 z-index 都在自己的容器內，
  // 只要這個容器本身建立一個較低的堆疊脈絡就夠了。
  zIndex: 0,
};

/** 資訊卡外框：與訂單詳情的 DCard 同一套換皮邏輯，但不吃內距（收合態的地圖要出血）。 */
const TrackingCard = ({ children }: { children: React.ReactNode }) =>
  isGasSkinV2() ? (
    <SkinCard padded={false}>
      <YStack br={24} overflow="hidden">
        {children}
      </YStack>
    </SkinCard>
  ) : (
    <YStack bg={colors.light} br={12} overflow="hidden" shadowColor="rgba(0,0,0,0.05)" shadowRadius={5}>
      {children}
    </YStack>
  );

/**
 * 位置新鮮度的重算節拍。
 *
 * buildTrackingDisplay 的「過期」判斷是拿 Date.now() 跟座標的回報時間比，
 * 但這棵樹只有在收到新座標時才重繪——而收到新座標本身就代表位置不過期。
 * 沒有這個 ticker，「位置更新於 N 分鐘前」與 15 分鐘收卡這兩條路徑，
 * 在它們唯一該生效的情境（配送員關掉定位）反而永遠不會成立：畫面會定格在
 * 最後一筆位置，文案還停在「即將抵達」。
 *
 * 30 秒一次即可：門檻是 3 分鐘與 15 分鐘，誤差半分鐘沒有意義。
 */
const STALE_TICK_MS = 30_000;

const useStaleTick = (active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((n) => n + 1), STALE_TICK_MS);
    return () => clearInterval(id);
  }, [active]);
};

interface DeliveryTrackingProps {
  orderId?: string;
  /** 僅在「配送中」時傳 true；其他狀態整塊不該存在，也不該打 API */
  isDelivering: boolean;
  /** 配送地址座標（order.address_snapshot_json 的經緯度） */
  destination: LatLngPoint | null;
  /**
   * 聯絡電話。是瓦斯行代表號，不是配送員的門號——courier 表沒有電話欄位，
   * 而且員工私人門號本來就不該因為接了單就交給客戶。
   */
  contactPhone?: string | null;
  xContext?: string | null;
}

const DeliveryTracking: React.FC<DeliveryTrackingProps> = ({ orderId, isDelivering, destination, contactPhone, xContext }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { tracking, unavailable } = useDriverLocation({ orderId, enabled: isDelivering, xContext });
  const setHeroActive = useTrackingHero((st) => st.setActive);
  // 有座標在畫面上就要持續重算它還新不新鮮（見 useStaleTick）
  useStaleTick(isDelivering && !unavailable && !!tracking?.position);
  const display = buildTrackingDisplay(tracking, destination);
  const skinV2 = isGasSkinV2();

  const position = tracking?.position ?? null;
  const driverPoint = position ? { lat: position.lat, lng: position.lng } : null;
  const hasMap = isDelivering && !unavailable && display.visible && display.showMap && !!driverPoint;

  // 回報給版面層：頁首現在是不是一張滿版地圖。
  // PageWrapper 據此收掉標題列的讓位內距，並讓標題列在捲到頂時不畫底色。
  useEffect(() => {
    setHeroActive(hasMap);
    return () => setHeroActive(false);
  }, [hasMap, setHeroActive]);

  if (!isDelivering || unavailable || !display.visible) return null;

  const driver = tracking?.driver ?? null;

  const statusRow = (
    <XStack px={18} pt={16} pb={hasMap && driver ? 14 : 16} gap={10} ai="center">
      <Circle size={32} bg={colors.primaryActive} ai="center" jc="center" flexShrink={0}>
        <RiTruckLine size={17} color={colors.primary} />
      </Circle>
      <YStack f={1} minWidth={0} gap={2}>
        <Text fontSize={hasMap ? 15 : 14} fow="700" color={skinV2 ? v2.title : undefined}>
          {display.headline}
        </Text>
        {display.sub ? (
          <Text fontSize={12.5} lineHeight={16} color={skinV2 ? v2.sub : colors.gray}>
            {display.sub}
          </Text>
        ) : null}
      </YStack>
    </XStack>
  );

  const driverRow = driver ? (
    <XStack
      px={18}
      py={12}
      ai="center"
      jc="space-between"
      gap={10}
      borderTopWidth={1}
      borderTopColor={skinV2 ? v2.divider : colors.borderLight}>
      <XStack ai="center" gap={10} f={1} minWidth={0}>
        <Circle size={30} bg={skinV2 ? v2.chipBg : colors.bgGray1} ai="center" jc="center" flexShrink={0}>
          <Text fontSize={13} fow="700" color={skinV2 ? v2.chipText : colors.gray}>
            {driver.name.slice(0, 1)}
          </Text>
        </Circle>
        <Text fontSize={13} fow="600" f={1} minWidth={0} numberOfLines={1}>
          {driver.name}
        </Text>
      </XStack>
      {contactPhone ? (
        <XStack
          tag="a"
          // tag="a" + href：撥號交給系統處理，不自己開視窗
          href={toTelHref(contactPhone)}
          ai="center"
          gap={5}
          px={12}
          py={7}
          br={999}
          bg={colors.primaryActive}
          flexShrink={0}
          pressStyle={{ opacity: 0.75 }}
          style={{ textDecoration: "none" }}>
          <RiPhoneLine size={14} color={colors.primary} />
          {/* 明寫「瓦斯行」：這顆鈕不是打給配送員，客戶按下去要知道會接通誰 */}
          <Text fontSize={12.5} fow="700" color={colors.primary}>
            聯絡瓦斯行
          </Text>
        </XStack>
      ) : null}
    </XStack>
  ) : null;

  return (
    <>
      {hasMap ? (
        <YStack>
          <div style={{ ...fullBleed, height: HERO_HEIGHT }}>
            <TrackingMap
              driver={driverPoint}
              bearing={position?.bearing}
              ts={position?.ts}
              destination={destination}
              faded={display.staleMinutes > 0}
              showRouteHint={display.showRouteHint}
              /* 全螢幕展開時這張被整個蓋住，沒必要再跑一份逐幀動畫 */
              animate={!expanded}
              height="100%"
              /* 上緣讓開浮在地圖上的標題列（約 90px，取 104 留餘裕）、
                 下緣讓開疊上來的資訊卡；不讓的話兩個 marker 會剛好躲在它們底下。 */
              fitPadding={{ top: 104, bottom: 72, left: 40, right: 40 }}
            />
            {/*
              返回鈕。地圖佔滿上半頁時整條標題列會隱形（見 all.scss 的 .is-over-hero），
              所以退出的入口必須自己長在地圖上——否則這一頁就走不掉了。
              一捲動標題列就回來，那時這顆鈕也隨地圖捲出畫面，不會有兩個返回並存。
            */}
            <View
              position="absolute"
              top="calc(12px + env(safe-area-inset-top))"
              left={12}
              w={38}
              h={38}
              br={999}
              bg={colors.light}
              ai="center"
              jc="center"
              cursor="pointer"
              pressStyle={{ opacity: 0.7 }}
              shadowColor="rgba(0,0,0,0.28)"
              shadowRadius={8}
              zIndex={800}
              onPress={() => navigate(-1)}>
              <RiArrowLeftSLine size={24} color={colors.text} />
            </View>
            {/*
              展開入口。地圖變大之後客戶一定會想放大／拖曳，但這張地圖包在頁面的
              ScrollView 裡不能吃手勢（會讓整頁滑不動），所以入口要比圖示按鈕更明確——
              直接寫「展開地圖」，讓人知道要去哪裡才能操作。
              位置留在資訊卡上緣之上，避免被那張卡壓住。
            */}
            <XStack
              position="absolute"
              bottom={44}
              right={16}
              /* Leaflet 的圖層 pane 最高到 700（popup），而這顆按鈕跟地圖同在一個堆疊脈絡裡，
                 不明寫就會被蓋掉——它在畫面上存在、但看不見。 */
              zIndex={800}
              ai="center"
              gap={6}
              px={12}
              py={8}
              br={999}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.75 }}
              shadowColor="rgba(0,0,0,0.28)"
              shadowRadius={8}
              onPress={() => setExpanded(true)}>
              <RiFullscreenLine size={15} color={colors.text} />
              <Text fontSize={12.5} fow="700" color={colors.text}>
                展開地圖
              </Text>
            </XStack>
          </div>

          {/* 資訊卡疊在地圖下緣：讓地圖看起來是延伸到卡片底下的一整片，而不是被切齊的一塊；
              同時也把「地圖」與「這句話在講這張地圖」綁在一起。 */}
          <YStack mt={-28} zIndex={1}>
            <TrackingCard>
              {statusRow}
              {driverRow}
            </TrackingCard>
          </YStack>
        </YStack>
      ) : (
        /* 還沒有座標：只有一行字，維持一般卡片大小。
           這種時候把版面讓給一張空地圖，是拿半個螢幕去顯示「還不知道」。 */
        <TrackingCard>
          {statusRow}
          {driverRow}
        </TrackingCard>
      )}

      <TrackingSheet
        isOpen={expanded}
        onOpenChange={setExpanded}
        driver={driver}
        contactPhone={contactPhone}
        driverPoint={driverPoint}
        bearing={position?.bearing}
        ts={position?.ts}
        destination={destination}
        display={display}
        showRouteHint={display.showRouteHint}
      />
    </>
  );
};

export default DeliveryTracking;
